import * as React from 'react';
import { Location } from 'history';
import Text from 'antd/lib/typography/Text';
import { ColumnType } from 'antd/lib/table';
import { Destination } from '@/types/destinations';
import {
    AppPlatform,
    AppStoreType,
    ReleaseStepType,
    ReleaseStatus,
    ReleaseTimelinePhase,
    DestinationPhasedRelease,
    Integration,
    ReleaseStepStatus,
} from '@/types';
import {
    PageViewType,
    TabularLayoutType,
    CombinedLayoutValue,
    SummaryViewPropertyBase,
    SummaryViewProperty,
    SummaryFetchContext,
    VersionSummaryViewDataItemAPI,
    SummaryViewDataRowAPI,
    SummaryViewTableRow,
    StatusSummaryDataAPI,
    MULTI_INSTANCE_ARCHETYPES,
    ARCHETYPE_PREFIX,
    STATISTIC_PREFIX,
    UseStandardViewColumnsParams,
    UseCompactViewParams,
    OrgSummaryPreferencesSnapshot,
    OrgDestinationsResponse,
    PageViewContext,
} from '@/types/summaryView';
import { Statistic } from '@/types';
import { StepRunStatusData } from '@/types/stepRunStatus';
import { WorkflowRunOverviewDisplayStatus } from '@/types/workflowOverview';
import {
    displayTextForWorkflowRunOverviewDisplayStatus,
    statusStringForReleaseStatus,
    displayStringForDestination,
} from '@/helpers/displayHelpers';
import { summaryViewFilterPropertiesFrom } from '@/helpers/trackingHelpers';
import * as AnalyticEvents from '@/utils/analytics';
import { AppEntity, OrganizationEntity, User } from '@/types';
import { dayjs } from '@/extendedDayjs';
import { iconAndAltForIntegrationType } from '@/helpers/integrationHelpers';
import { compareVersions } from '@/helpers';
import { antGrey } from '@/components/styles/globalStyles';
import { DestinationCell, renderColumnTitle } from '@/components/orgSummary/OverviewTableCellComponents';

export const getCombinedLayoutValue = (
    pageView: PageViewType,
    tabularLayout: TabularLayoutType,
): CombinedLayoutValue => {
    if (pageView === PageViewType.Default) return 'summary';
    return tabularLayout === TabularLayoutType.Compact ? 'compact' : 'standard';
};

export const handleCombinedLayoutChange = (
    value: CombinedLayoutValue,
    updatePreferences: (changes: { pageView?: PageViewType; tabularLayout?: TabularLayoutType }) => void,
) => {
    if (value === 'summary') {
        updatePreferences({ pageView: PageViewType.Default });
    } else {
        updatePreferences({
            pageView: PageViewType.Tabular,
            tabularLayout: value === 'compact' ? TabularLayoutType.Compact : TabularLayoutType.Standard,
        });
    }
};

export function archetypeToSummaryViewProperty(archetype: string): string {
    return `${ARCHETYPE_PREFIX}:${archetype}`;
}

export function isArchetypeProperty(property: string): boolean {
    return property.startsWith(`${ARCHETYPE_PREFIX}:`);
}

export function getArchetypeFromProperty(property: string): string | null {
    if (!isArchetypeProperty(property)) {
        return null;
    }
    return property.substring(`${ARCHETYPE_PREFIX}:`.length);
}

export function statisticToSummaryViewProperty(statistic: string): string {
    return `${STATISTIC_PREFIX}:${statistic}`;
}

export function isStatisticProperty(property: string): boolean {
    return property.startsWith(`${STATISTIC_PREFIX}:`);
}

export function getStatisticFromProperty(property: string): string | null {
    if (!isStatisticProperty(property)) {
        return null;
    }
    return property.substring(`${STATISTIC_PREFIX}:`.length);
}

export function isSummaryViewProperty(value: string): value is SummaryViewProperty {
    return Object.values(SummaryViewPropertyBase).includes(value as SummaryViewPropertyBase);
}

export function getSummaryViewPropertyLabel(property: string): string {
    if (isArchetypeProperty(property)) {
        const archetype = getArchetypeFromProperty(property);
        switch (archetype) {
            case ReleaseStepType.Kickoff:
                return 'Kickoff';
            case ReleaseStepType.FeatureReadiness:
                return 'Feature readiness';
            case ReleaseStepType.ReleaseCandidate:
                return 'Release candidate';
            case ReleaseStepType.RegressionTesting:
                return 'Regression testing';
            case ReleaseStepType.BetaTesting:
                return 'Beta testing';
            case ReleaseStepType.Metadata:
                return 'Metadata';
            case ReleaseStepType.Screenshots:
                return 'Screenshots';
            case ReleaseStepType.Approvals:
                return 'Approvals';
            case ReleaseStepType.Submission:
                return 'App submission';
            case ReleaseStepType.StoreReview:
                return 'App store review';
            case ReleaseStepType.Takeoff:
                return 'Release';
            case ReleaseStepType.CIDistribution:
                return 'CI distribution';
            case ReleaseStepType.VCSDistribution:
                return 'VCS distribution';
            case ReleaseStepType.RollbackBuild:
                return 'Rollback build';
            case ReleaseStepType.Translations:
                return 'Translations';
            case ReleaseStepType.Rollout:
                return 'Rollout';
            case ReleaseStepType.ReleaseCycle:
                return 'Release cycle';
            default:
                return archetype ?? property;
        }
    }

    if (isStatisticProperty(property)) {
        const statistic = getStatisticFromProperty(property);
        return (statistic ?? property)
            .replace(/([A-Z])/g, ' $1')
            .replace(/^./, (str) => str.toUpperCase())
            .trim();
    }

    switch (property as SummaryViewPropertyBase) {
        case SummaryViewPropertyBase.WorkflowRunVersion:
            return 'Version';
        case SummaryViewPropertyBase.ReleasePilot:
            return 'Release pilot';
        case SummaryViewPropertyBase.Status:
            return 'Status';
        case SummaryViewPropertyBase.FeatureCompletePercent:
            return 'Feature complete %';
        case SummaryViewPropertyBase.TargetKickoffDates:
            return 'Target kickoff dates';
        case SummaryViewPropertyBase.TargetSubmitDates:
            return 'Target submit dates';
        case SummaryViewPropertyBase.TargetReleaseDates:
            return 'Target release dates';
        case SummaryViewPropertyBase.KickedOffDate:
            return 'Kicked off at';
        case SummaryViewPropertyBase.SubmittedDate:
            return 'Submitted at';
        case SummaryViewPropertyBase.ReleasedAtDate:
            return 'Released at';
        case SummaryViewPropertyBase.Builds:
            return 'Builds';
        case SummaryViewPropertyBase.PhasedRelease:
            return 'Phased release';
        case SummaryViewPropertyBase.AdoptionRate:
            return 'Adoption rate';
        case SummaryViewPropertyBase.CrashFreeUsers:
            return 'Crash free users';
        case SummaryViewPropertyBase.CrashFreeSessions:
            return 'Crash free sessions';
        default:
            return property;
    }
}

export function isBasicPropertyRow(
    row: SummaryViewDataRowAPI,
): row is Extract<SummaryViewDataRowAPI, { type: SummaryViewProperty }> {
    return isSummaryViewProperty(row.type);
}

export function isArchetypePropertyRow(
    row: SummaryViewDataRowAPI,
): row is Extract<SummaryViewDataRowAPI, { data: StepRunStatusData }> {
    return isArchetypeProperty(row.type);
}

export function isStatisticPropertyRow(
    row: SummaryViewDataRowAPI,
): row is Extract<SummaryViewDataRowAPI, { data: Statistic }> {
    return isStatisticProperty(row.type);
}

export const processDestinations = (orgDestinationsData: any, appId?: string): Destination[] => {
    if (!orgDestinationsData?.appDestinations) return [];

    const destinations: Destination[] = [];
    const appDestinations =
        appId ?
            orgDestinationsData.appDestinations.filter((app: any) => app.appId === appId)
        :   orgDestinationsData.appDestinations;

    appDestinations.forEach((app: any) => {
        app.destinations?.forEach((dest: any) => {
            destinations.push({
                stepConfigId: dest.stepConfigId,
                name: dest.name,
                platform: dest.platform as AppPlatform,
                appStoreType: dest.appStoreType as AppStoreType,
                iconURL: dest.iconURL,
                appName: app.appName,
            });
        });
    });

    return destinations;
};

export const processVersions = (orgDestinationsData: any, appId?: string): string[] => {
    if (!orgDestinationsData) return [];

    if (appId) {
        const appData = orgDestinationsData.appDestinations?.find((app: any) => app.appId === appId);
        return appData?.versions ? [...appData.versions] : [];
    }

    return orgDestinationsData.allVersions ? [...orgDestinationsData.allVersions] : [];
};

const ALWAYS_VISIBLE_METRICS = [SummaryViewPropertyBase.WorkflowRunVersion];

export const getAvailableMetricKeys = (supportedStatisticTypes: string[]) => {
    const baseMetrics = Object.values(SummaryViewPropertyBase).filter(
        (property) => !ALWAYS_VISIBLE_METRICS.includes(property),
    );
    const archetypeMetrics = Object.values(ReleaseStepType).map((archetype) =>
        archetypeToSummaryViewProperty(archetype),
    );
    const statisticMetrics = supportedStatisticTypes.map((statistic) => statisticToSummaryViewProperty(statistic));

    return [...baseMetrics, ...archetypeMetrics, ...statisticMetrics];
};

export const getAvailableMetricOptions = (supportedStatisticTypes: string[]): Array<{ key: string; label: string }> => {
    const baseMetrics = Object.values(SummaryViewPropertyBase)
        .filter((property) => !ALWAYS_VISIBLE_METRICS.includes(property))
        .map((property) => ({
            key: property,
            label: getSummaryViewPropertyLabel(property),
        }));
    const archetypeMetrics = Object.values(ReleaseStepType).map((archetype) => ({
        key: archetypeToSummaryViewProperty(archetype),
        label: archetype,
    }));
    const statisticMetrics = supportedStatisticTypes.map((statistic) => ({
        key: statisticToSummaryViewProperty(statistic),
        label: statistic,
    }));

    return [...baseMetrics, ...archetypeMetrics, ...statisticMetrics];
};

export const buildFetchRequest = (context: SummaryFetchContext) => {
    const { snapshot } = context;

    switch (context.kind) {
        case 'full':
            return {
                versions: snapshot.selectedVersions,
                destinationIds: snapshot.selectedDestinationIds,
                properties: snapshot.selectedMetrics,
            };
        case 'metrics':
            return {
                versions: snapshot.selectedVersions,
                destinationIds: snapshot.selectedDestinationIds,
                properties: context.metrics || [],
            };
        case 'versions':
            return {
                versions: context.versions || [],
                destinationIds: snapshot.selectedDestinationIds,
                properties: snapshot.selectedMetrics,
            };
        case 'destinations':
            return {
                versions: snapshot.selectedVersions,
                destinationIds: context.destinations || [],
                properties: snapshot.selectedMetrics,
            };
    }
};

export const createFilterContexts = (
    addedMetrics: string[],
    addedVersions: string[],
    snapshot: SummaryFetchContext['snapshot'],
): SummaryFetchContext[] => {
    const contexts: SummaryFetchContext[] = [];

    if (addedMetrics.length > 0) {
        contexts.push({
            kind: 'metrics',
            metrics: addedMetrics,
            snapshot,
            source: 'filters',
        });
    }

    if (addedVersions.length > 0) {
        contexts.push({
            kind: 'versions',
            versions: addedVersions,
            snapshot,
            source: 'filters',
        });
    }

    return contexts;
};

export const createDestinationContext = (
    addedDestinations: string[],
    snapshot: SummaryFetchContext['snapshot'],
): SummaryFetchContext[] => {
    if (addedDestinations.length > 0) {
        return [
            {
                kind: 'destinations',
                destinations: addedDestinations,
                snapshot,
                source: 'filters',
            },
        ];
    }
    return [];
};

export const parseListParam = (param: string | null): string[] | null => {
    if (!param) return null;
    return param.split(',').filter(Boolean);
};

export interface StoredPreferences {
    selectedMetrics?: string[];
    selectedVersions?: string[];
    selectedDestinationIds?: string[];
}

export interface ResolvedPreferences {
    versions: string[];
    destinationIds: string[];
    metrics: string[];
}

export const resolveInitialPreferences = (
    stored: StoredPreferences,
    available: { versions: string[]; destinationIds: string[]; metricKeys: string[] },
    defaults: { versions: string[]; destinationIds: string[]; metrics: string[] },
): ResolvedPreferences => {
    const urlParams = new URLSearchParams(window.location.search);

    const versionsFromUrl = parseListParam(urlParams.get('versions'));
    const versionsFromStorage = stored.selectedVersions;
    const preferredVersions = versionsFromUrl ?? versionsFromStorage ?? [];
    const validVersions = preferredVersions.filter((v) => available.versions.includes(v));
    const versions = validVersions.length > 0 ? validVersions : defaults.versions;

    const destinationsFromUrl = parseListParam(urlParams.get('destinations'));
    const destinationsFromStorage = stored.selectedDestinationIds;
    const preferredDestinations = destinationsFromUrl ?? destinationsFromStorage ?? [];
    const validDestinations = preferredDestinations.filter((d) => available.destinationIds.includes(d));
    const destinationIds = validDestinations.length > 0 ? validDestinations : defaults.destinationIds;

    const metricsFromUrl = parseListParam(urlParams.get('metrics'));
    const metricsFromStorage = stored.selectedMetrics;
    const preferredMetrics = metricsFromUrl ?? metricsFromStorage ?? [];
    const validMetrics = preferredMetrics.filter((m) => available.metricKeys.includes(m));
    const metrics = validMetrics.length > 0 ? validMetrics : defaults.metrics;

    return { versions, destinationIds, metrics };
};

export const resolveVersionSelection = (preferred: string[] | null | undefined, fallback: string[] = []): string[] => {
    if (preferred !== null && preferred !== undefined) {
        return dedupePreserveOrder(preferred);
    }
    return dedupePreserveOrder(fallback);
};

export const filterToAvailable = (selected: string[] | null, available: string[]): string[] => {
    if (!selected || selected.length === 0) return available;
    if (available.length === 0) return selected;
    const filtered = selected.filter((item) => available.includes(item));
    return filtered.length > 0 ? filtered : available;
};

export const dedupePreserveOrder = (items: string[]): string[] => {
    return Array.from(new Set(items));
};

export const normalizePageView = (value?: string | null): PageViewType => {
    if (!value) return PageViewType.Default;
    switch (value) {
        case PageViewType.Tabular:
        case 'tabular':
            return PageViewType.Tabular;
        case PageViewType.Default:
        case 'default':
        default:
            return PageViewType.Default;
    }
};

export const normalizeTabularLayout = (value?: string | null): TabularLayoutType => {
    if (!value) return TabularLayoutType.Standard;
    switch (value) {
        case TabularLayoutType.Compact:
        case 'compact':
            return TabularLayoutType.Compact;
        case TabularLayoutType.Standard:
        case 'standard':
        default:
            return TabularLayoutType.Standard;
    }
};

export const mergePreferences = (
    changes: {
        pageView?: PageViewType;
        tabularLayout?: TabularLayoutType;
        metrics?: string[];
        versions?: string[];
        showLive?: boolean;
        showNext?: boolean;
        destinations?: string[];
    },
    current: {
        pageView: PageViewType;
        tabularLayout: TabularLayoutType;
        selectedMetrics: string[];
        selectedVersions: string[];
        showLive?: boolean;
        showNext?: boolean;
        selectedDestinationIds: string[];
    },
    available: {
        metrics: string[];
        versions: string[];
        destinationIds: string[];
    },
) => {
    const nextPageView = normalizePageView(changes.pageView ?? current.pageView);
    const nextTabularLayout = normalizeTabularLayout(changes.tabularLayout ?? current.tabularLayout);
    const mergedMetrics = changes.metrics ?? current.selectedMetrics;
    const mergedVersions = changes.versions ?? current.selectedVersions;
    const mergedDestinations = changes.destinations ?? current.selectedDestinationIds;
    const nextShowLive = changes.showLive !== undefined ? changes.showLive : current.showLive;
    const nextShowNext = changes.showNext !== undefined ? changes.showNext : current.showNext;

    const nextMetrics = filterToAvailable(mergedMetrics, available.metrics);
    const nextVersions = resolveVersionSelection(mergedVersions, available.versions);
    const nextDestinations = filterToAvailable(mergedDestinations, available.destinationIds);

    return {
        pageView: nextPageView,
        tabularLayout: nextTabularLayout,
        selectedMetrics: nextMetrics,
        selectedVersions: nextVersions,
        showLive: nextShowLive,
        showNext: nextShowNext,
        selectedDestinationIds: nextDestinations,
    };
};

export const buildUrlSearchParams = (
    preferences: {
        pageView?: PageViewType;
        tabularLayout?: TabularLayoutType;
        selectedMetrics?: string[];
        selectedVersions?: string[];
        showLive?: boolean;
        showNext?: boolean;
        selectedDestinationIds?: string[];
    },
    location: Location,
): URLSearchParams => {
    const params = new URLSearchParams(location.search);

    if (preferences.pageView === PageViewType.Tabular) {
        params.set('pageView', PageViewType.Tabular);
        if (preferences.tabularLayout) {
            params.set('tabularLayout', preferences.tabularLayout);
        }
    } else {
        params.delete('pageView');
        params.delete('tabularLayout');
    }

    if (preferences.selectedMetrics?.length) {
        params.set('metrics', preferences.selectedMetrics.join(','));
    }
    if (preferences.selectedVersions?.length) {
        params.set('versions', preferences.selectedVersions.join(','));
    }

    const setOrDeleteParam = (key: string, value: boolean | undefined) => {
        if (value !== undefined) {
            value ? params.set(key, 'true') : params.delete(key);
        }
    };

    setOrDeleteParam('showLive', preferences.showLive);
    setOrDeleteParam('showNext', preferences.showNext);

    if (preferences.selectedDestinationIds?.length) {
        params.set('destinations', preferences.selectedDestinationIds.join(','));
    }

    return params;
};

export const isMultiInstanceArchetypeProperty = (propertyKey: string): boolean => {
    const archetype = getArchetypeFromProperty(propertyKey) as ReleaseStepType | null;
    if (!archetype) {
        return false;
    }
    return MULTI_INSTANCE_ARCHETYPES.has(archetype);
};

export const transformToTableRows = (items: VersionSummaryViewDataItemAPI[]): SummaryViewTableRow[] => {
    return items.map((item) => {
        const row: SummaryViewTableRow = {
            key: `${item.version}-${item.destinationId}`,
            version: item.version,
            destinationId: item.destinationId,
        };

        item.rows.forEach((dataRow: SummaryViewDataRowAPI) => {
            const propertyKey = dataRow.type;

            if (isArchetypePropertyRow(dataRow)) {
                row[propertyKey] = dataRow.data;

                if (isMultiInstanceArchetypeProperty(propertyKey) && dataRow.data?.stepConfigId) {
                    const uniqueKey = `${propertyKey}:${dataRow.data.stepConfigId}`;
                    row[uniqueKey] = dataRow.data;
                }
            } else {
                row[propertyKey] = dataRow.data;
            }
        });

        return row;
    });
};

export const deriveWorkflowStatus = (
    status: ReleaseStatus,
    timelinePhase: ReleaseTimelinePhase,
    phasedReleaseInfo?: DestinationPhasedRelease,
): WorkflowRunOverviewDisplayStatus | null => {
    const isLiveOrPhased = phasedReleaseInfo && (phasedReleaseInfo.phasedReleasePercent ?? 0) > 0;
    const isSkipped = status === 'skipped';
    const isFullyRolledOut = phasedReleaseInfo && (phasedReleaseInfo.phasedReleasePercent ?? 0) >= 100;

    if (isSkipped) {
        return WorkflowRunOverviewDisplayStatus.Skipped;
    }

    if (isLiveOrPhased && !isFullyRolledOut) {
        return WorkflowRunOverviewDisplayStatus.RollingOut;
    }

    if (isFullyRolledOut) {
        return WorkflowRunOverviewDisplayStatus.Completed;
    }

    if (!isLiveOrPhased && timelinePhase === 'upcoming') {
        return WorkflowRunOverviewDisplayStatus.Upcoming;
    }

    if (!isLiveOrPhased && timelinePhase === 'current') {
        return WorkflowRunOverviewDisplayStatus.Active;
    }

    return null;
};

export const getPrimaryStatusLabel = (data?: StatusSummaryDataAPI): string => {
    if (!data) return '';
    const workflowStatus = deriveWorkflowStatus(data.status, data.timelinePhase, data.phasedReleaseInfo);
    if (workflowStatus) {
        return displayTextForWorkflowRunOverviewDisplayStatus(workflowStatus);
    }
    return statusStringForReleaseStatus(data.status);
};

export const sortByDate = (a: SummaryViewTableRow, b: SummaryViewTableRow, datePropertyKey: string) => {
    const aDate = a[datePropertyKey];
    const bDate = b[datePropertyKey];
    if (!aDate) return 1;
    if (!bDate) return -1;
    return dayjs(aDate).diff(dayjs(bDate));
};

const STEP_STATUS_PRIORITY: Record<ReleaseStepStatus, number> = {
    [ReleaseStepStatus.Blocked]: 1,
    [ReleaseStepStatus.Pending]: 2,
    [ReleaseStepStatus.PendingChecklistOnly]: 2,
    [ReleaseStepStatus.Ready]: 3,
    [ReleaseStepStatus.Inactive]: 4,
    [ReleaseStepStatus.Skipped]: 5,
};

export const getStepStatusSortPriority = (status?: ReleaseStepStatus): number => {
    return status ? (STEP_STATUS_PRIORITY[status] ?? 5) : 5;
};

export const getIconForMetricKey = (
    metricKey: string,
    appStoreIntegration?: Integration,
    stabilityIntegration?: Integration,
): [string?, string?] => {
    if (metricKey === 'PhasedRelease') {
        return iconAndAltForIntegrationType(appStoreIntegration?.id);
    }
    if (metricKey === 'AdoptionRate') {
        return iconAndAltForIntegrationType(stabilityIntegration?.id);
    }
    return [undefined, undefined];
};

export const createNullSafeSorter = <T, V>(
    getValue: (row: T) => V | null | undefined,
    compareFn: (a: V, b: V) => number,
) => {
    return (a: T, b: T): number => {
        const aValue = getValue(a);
        const bValue = getValue(b);
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;
        return compareFn(aValue, bValue);
    };
};

export const createStringSorter = <T,>(getValue: (row: T) => string | null | undefined) =>
    createNullSafeSorter<T, string>(getValue, (a, b) => a.localeCompare(b, undefined, { numeric: true }));

export const createNumberSorter = <T,>(getValue: (row: T) => number | null | undefined, defaultValue = 0) => {
    return (a: T, b: T): number => {
        const aValue = getValue(a);
        const bValue = getValue(b);
        return (aValue ?? defaultValue) - (bValue ?? defaultValue);
    };
};

export const createDateSorter = <T,>(getValue: (row: T) => string | number | Date | null | undefined) =>
    createNullSafeSorter<T, number>(
        (row) => {
            const value = getValue(row);
            if (!value) return undefined;
            return new Date(value).getTime();
        },
        (a, b) => a - b,
    );

export const createPercentageSorter = <T,>(getValue: (row: T) => number | null | undefined, defaultValue = 0) =>
    createNumberSorter(getValue, defaultValue);

export const createKeyedStringSorter = <T extends Record<string, any>>(key: keyof T) =>
    createStringSorter<T>((row) => row[key] as string | null | undefined);

export const createKeyedNumberSorter = <T extends Record<string, any>>(key: keyof T, defaultValue = 0) =>
    createNumberSorter<T>((row) => row[key] as number | null | undefined, defaultValue);

export const createKeyedPercentageSorter = <T extends Record<string, any>>(
    key: keyof T,
    percentField: string,
    defaultValue = 0,
) =>
    createPercentageSorter<T>((row) => {
        const value = row[key] as any;
        return value ? (value[percentField] as number | undefined) : undefined;
    }, defaultValue);

// Table column/row creation helpers

export const createSorterForMetric = (metricKey: string, destinationsById?: Record<string, Destination>) => {
    switch (metricKey) {
        case SummaryViewPropertyBase.WorkflowRunVersion:
            return (a: SummaryViewTableRow, b: SummaryViewTableRow) => {
                if (!a.version || !b.version) return 0;
                return -compareVersions(a.version, b.version);
            };
        case SummaryViewPropertyBase.Status:
            return (a: SummaryViewTableRow, b: SummaryViewTableRow) => {
                const aStatus = a[SummaryViewPropertyBase.Status] as StatusSummaryDataAPI | undefined;
                const bStatus = b[SummaryViewPropertyBase.Status] as StatusSummaryDataAPI | undefined;

                if (!aStatus && !bStatus) return 0;
                if (!aStatus) return 1;
                if (!bStatus) return -1;

                const aLabel = getPrimaryStatusLabel(aStatus);
                const bLabel = getPrimaryStatusLabel(bStatus);
                return aLabel.localeCompare(bLabel);
            };
        case 'destination':
            return (a: SummaryViewTableRow, b: SummaryViewTableRow) => {
                if (!destinationsById) return a.destinationId.localeCompare(b.destinationId);

                const aDestination = destinationsById[a.destinationId];
                const bDestination = destinationsById[b.destinationId];

                if (!aDestination && !bDestination) return 0;
                if (!aDestination) return 1;
                if (!bDestination) return -1;

                const aDisplayName = displayStringForDestination(
                    aDestination.platform,
                    aDestination.appStoreType,
                    aDestination.name,
                    aDestination.appName,
                );
                const bDisplayName = displayStringForDestination(
                    bDestination.platform,
                    bDestination.appStoreType,
                    bDestination.name,
                    bDestination.appName,
                );

                return aDisplayName.localeCompare(bDisplayName);
            };
        case SummaryViewPropertyBase.Builds:
            return (a: SummaryViewTableRow, b: SummaryViewTableRow) => {
                const aBuilds = a[SummaryViewPropertyBase.Builds];
                const bBuilds = b[SummaryViewPropertyBase.Builds];
                const aBuildNumber = aBuilds?.[0]?.appStoreBuild?.buildNumber || '';
                const bBuildNumber = bBuilds?.[0]?.appStoreBuild?.buildNumber || '';
                if (!aBuildNumber) return 1;
                if (!bBuildNumber) return -1;
                return aBuildNumber.localeCompare(bBuildNumber, undefined, { numeric: true });
            };
        case SummaryViewPropertyBase.ReleasePilot:
            return createKeyedStringSorter<SummaryViewTableRow>(SummaryViewPropertyBase.ReleasePilot);
        case SummaryViewPropertyBase.FeatureCompletePercent:
            return (a: SummaryViewTableRow, b: SummaryViewTableRow) => {
                const aStep = a[SummaryViewPropertyBase.FeatureCompletePercent];
                const bStep = b[SummaryViewPropertyBase.FeatureCompletePercent];
                if (!aStep) return 1;
                if (!bStep) return -1;
                return (bStep.percentFeatureComplete ?? 0) - (aStep.percentFeatureComplete ?? 0);
            };
        case SummaryViewPropertyBase.TargetKickoffDates:
        case SummaryViewPropertyBase.TargetSubmitDates:
        case SummaryViewPropertyBase.TargetReleaseDates:
        case SummaryViewPropertyBase.KickedOffDate:
        case SummaryViewPropertyBase.SubmittedDate:
        case SummaryViewPropertyBase.ReleasedAtDate:
            return (a: SummaryViewTableRow, b: SummaryViewTableRow) => sortByDate(a, b, metricKey);
        case SummaryViewPropertyBase.PhasedRelease:
            return createKeyedPercentageSorter<SummaryViewTableRow>(
                SummaryViewPropertyBase.PhasedRelease,
                'phasedReleasePercent',
            );
        case SummaryViewPropertyBase.CrashFreeUsers:
            return (a: SummaryViewTableRow, b: SummaryViewTableRow) => {
                const aCrashFreeUsers = a[SummaryViewPropertyBase.CrashFreeUsers];
                const bCrashFreeUsers = b[SummaryViewPropertyBase.CrashFreeUsers];
                return aCrashFreeUsers - bCrashFreeUsers;
            };
        case SummaryViewPropertyBase.CrashFreeSessions:
            return (a: SummaryViewTableRow, b: SummaryViewTableRow) => {
                const aCrashFreeSessions = a[SummaryViewPropertyBase.CrashFreeSessions];
                const bCrashFreeSessions = b[SummaryViewPropertyBase.CrashFreeSessions];
                return aCrashFreeSessions - bCrashFreeSessions;
            };
        case SummaryViewPropertyBase.AdoptionRate:
            return (a: SummaryViewTableRow, b: SummaryViewTableRow) => {
                const aAdoption = a[SummaryViewPropertyBase.AdoptionRate]?.[0]?.stabilityInfo?.sessionAdoption ?? 0;
                const bAdoption = b[SummaryViewPropertyBase.AdoptionRate]?.[0]?.stabilityInfo?.sessionAdoption ?? 0;
                return aAdoption - bAdoption;
            };
        default:
            if (isArchetypeProperty(metricKey)) {
                return (a: SummaryViewTableRow, b: SummaryViewTableRow) => {
                    const aPriority = getStepStatusSortPriority(a[metricKey]?.status);
                    const bPriority = getStepStatusSortPriority(b[metricKey]?.status);
                    return aPriority - bPriority;
                };
            }
            if (isStatisticProperty(metricKey)) {
                return (a: SummaryViewTableRow, b: SummaryViewTableRow) => {
                    const aValue = a[metricKey]?.value ?? 0;
                    const bValue = b[metricKey]?.value ?? 0;
                    return aValue - bValue;
                };
            }
            return undefined;
    }
};

export const createStandardViewColumns = (params: UseStandardViewColumnsParams): ColumnType<SummaryViewTableRow>[] => {
    const { metrics, currentAppId, destinationsById, appStoreIntegration, stabilityIntegration } = params;

    const destinationColumn: ColumnType<SummaryViewTableRow> = {
        title: renderColumnTitle('Destination name'),
        key: 'destination',
        fixed: 'left' as const,
        render: (_: any, row: SummaryViewTableRow) => {
            const dest = destinationsById[row.destinationId];
            if (!dest) {
                return '-';
            }
            return (
                <DestinationCell
                    destinationName={dest.name || ''}
                    appName={dest.appName}
                    appImageUrl={dest.iconURL || ''}
                    platform={dest.platform}
                    appStoreType={dest.appStoreType}
                />
            );
        },
        sorter: createSorterForMetric('destination', destinationsById),
        onCell: () => ({
            style: {
                backgroundColor: antGrey.grey2,
            },
        }),
    };

    const metricColumns = metrics.map((metric) => {
        const [iconSrc, iconAlt] = getIconForMetricKey(metric.key, appStoreIntegration, stabilityIntegration);
        const title = renderColumnTitle(metric.title, iconSrc, iconAlt);

        const columnDef: ColumnType<SummaryViewTableRow> = {
            title,
            key: metric.key,
            render: (_: any, row: SummaryViewTableRow) =>
                metric.renderCell({ row, currentAppId, appsById: destinationsById, compact: false }),
            sorter: createSorterForMetric(metric.key, destinationsById),
            filterSearch: metric.filterSearch,
            filters: metric.filters,
            onFilter: metric.onFilter,
        };

        if (metric.key === SummaryViewPropertyBase.WorkflowRunVersion) {
            columnDef.fixed = 'left' as const;
            columnDef.onCell = () => ({
                style: {
                    backgroundColor: antGrey.grey2,
                },
            });
        }

        if (metric.key === SummaryViewPropertyBase.AdoptionRate) {
            columnDef.width = '12rem';
        }

        if (metric.getCellStyle) {
            columnDef.onCell = (row: SummaryViewTableRow) => ({ style: metric.getCellStyle!(row) });
        }

        return columnDef;
    });

    return [destinationColumn, ...metricColumns];
};

export const createCompactViewConfig = (params: UseCompactViewParams) => {
    const { releasesData, metrics, currentAppId, destinationsById } = params;

    const columns = [
        {
            title: '',
            key: 'metric',
            dataIndex: 'metric',
            fixed: 'left' as const,
            width: 200,
            render: (text: string) => (
                <Text style={{ fontSize: 12, color: antGrey.grey7, fontWeight: 400 }}>{text}</Text>
            ),
            onCell: () => ({
                style: {
                    backgroundColor: antGrey.grey2,
                    fontWeight: 400,
                },
            }),
        },
        ...releasesData.map((row) => {
            const dest = destinationsById[row.destinationId];
            return {
                title: (
                    <DestinationCell
                        destinationName={dest?.name || ''}
                        appName={dest?.appName}
                        appImageUrl={dest?.iconURL || ''}
                        platform={dest?.platform}
                        appStoreType={dest?.appStoreType}
                    />
                ),
                key: row.key,
                dataIndex: row.key,
                onCell: (record: any) => {
                    const metric = metrics.find((m) => m.key === record.key);
                    if (metric?.getCellStyle) {
                        return {
                            style: metric.getCellStyle(row),
                        };
                    }
                    return {};
                },
                onHeaderCell: () => ({
                    style: {
                        backgroundColor: antGrey.grey2,
                    },
                }),
            };
        }),
    ];

    const rows = metrics
        .filter((metric) => metric.key !== 'destination')
        .map((metric) => {
            const row: any = {
                key: metric.key,
                metric: metric.title,
            };

            releasesData.forEach((dataRow) => {
                row[dataRow.key] = metric.renderCell({
                    row: dataRow,
                    currentAppId,
                    appsById: destinationsById,
                    compact: true,
                });
            });

            return row;
        });

    return { columns, rows };
};

export const buildViewTypeFromPreferences = (preferences: OrgSummaryPreferencesSnapshot): string => {
    const { pageView, tabularLayout } = preferences;
    return pageView === PageViewType.Default ? PageViewType.Default : `${pageView}-${tabularLayout}`;
};

export const createFilterApplyTracker = (
    org: OrganizationEntity,
    user: User,
    appsInOrg: string[],
    appGroupsInOrg: string[],
    preferences: OrgSummaryPreferencesSnapshot,
    pageContext: PageViewContext,
    app?: AppEntity,
) => {
    return (metrics: string[], versions: string[]) => {
        const viewType = buildViewTypeFromPreferences(preferences);
        const properties = summaryViewFilterPropertiesFrom(
            org,
            app,
            user,
            appsInOrg,
            appGroupsInOrg,
            { destinationIds: preferences.selectedDestinationIds, versions, attributes: metrics },
            { pageContext, viewType },
        );
        AnalyticEvents.track(AnalyticEvents.Event.ApplySummaryViewFilters, properties);
    };
};

export const getDestinationDisplayText = (
    selectedDestinationIds: string[],
    availableDestinations: Destination[],
): string | null => {
    if (selectedDestinationIds.length === 0) {
        return availableDestinations.length === 0 ? null : 'All destinations';
    }
    if (selectedDestinationIds.length === 1) {
        const selectedDestination = availableDestinations.find(
            (dest) => dest.stepConfigId === selectedDestinationIds[0],
        );
        return displayStringForDestination(
            selectedDestination?.platform,
            selectedDestination?.appStoreType,
            selectedDestination?.name,
        );
    }
    return selectedDestinationIds.length === availableDestinations.length ?
            'All destinations'
        :   'Selected destinations';
};

export const getActiveVersionsForDestinations = (
    orgData: OrgDestinationsResponse | undefined,
    destinationIds: string[],
): string[] => {
    if (!orgData?.appDestinations || destinationIds.length === 0) return [];

    const destinationIdSet = new Set(destinationIds);
    const activeVersions = new Set<string>();

    for (const app of orgData.appDestinations) {
        const appDestinationIds = app.destinations?.map((d) => d.stepConfigId) ?? [];
        const hasMatchingDestination = appDestinationIds.some((id) => destinationIdSet.has(id));

        if (hasMatchingDestination && app.activeVersion) {
            activeVersions.add(app.activeVersion);
        }
    }

    return Array.from(activeVersions);
};

export const getDefaultVersionsForDestinations = (availableVersions: string[]): string[] => {
    if (availableVersions.length === 0) return [];
    return [availableVersions[0]];
};

export const getValidVersionForDestinations = (
    destinationIds: string[],
    orgDestinationsData: OrgDestinationsResponse | undefined,
): string | null => {
    if (!orgDestinationsData?.appDestinations || destinationIds.length === 0) return null;

    for (const app of orgDestinationsData.appDestinations) {
        const appDestIds = app.destinations.map((d) => d.stepConfigId);
        if (destinationIds.some((id) => appDestIds.includes(id))) {
            return app.activeVersion || app.versions[0] || null;
        }
    }

    return null;
};
